import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Music2 } from "lucide-react";
import { api, API } from "../lib/api";
import { SEO } from "../components/SEO";
import { Reveal } from "../components/Reveal";

const Artistes = () => {
  const [artists, setArtists] = useState(null);

  useEffect(() => {
    api.get("/artistes").then((r) => setArtists(r.data)).catch(() => setArtists([]));
  }, []);

  return (
    <main data-testid="artistes-page" className="mx-auto max-w-7xl px-5 pb-24 pt-32 sm:px-8">
      <SEO title="Nos artistes" path="/artistes"
           description="Découvrez les artistes de La Console des Artistes : leurs univers, leurs morceaux disponibles en licence pour vos images." />
      <Reveal>
        <p className="text-xs uppercase tracking-[0.35em] text-amber-500">Le collectif</p>
        <h1 className="mt-3 font-display text-4xl font-black tracking-tighter sm:text-6xl">
          Nos <span className="italic text-amber-500">artistes</span>
        </h1>
        <p className="mt-6 max-w-xl text-sm leading-relaxed text-zinc-400">
          Des compositeurs sélectionnés par le studio. Chaque achat de licence les rémunère directement.
        </p>
      </Reveal>

      {!artists ? (
        <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-64 animate-pulse border border-white/[0.08] bg-surface" />
          ))}
        </div>
      ) : artists.length === 0 ? (
        <p className="mt-20 text-center text-sm uppercase tracking-[0.25em] text-zinc-500" data-testid="artistes-empty">
          Aucun artiste pour le moment
        </p>
      ) : (
        <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="artistes-grid">
          {artists.map((a, i) => (
            <motion.div
              key={a.slug}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.55, delay: (i % 3) * 0.08 }}
            >
              <Link
                to={`/artiste/${a.slug}`}
                data-testid={`artiste-card-${a.slug}`}
                className="group relative block aspect-[4/3] overflow-hidden border border-white/[0.08] bg-surface"
              >
                {a.first_track_id ? (
                  <img
                    src={`${API}/tracks/${a.first_track_id}/cover`}
                    alt={a.artist_name}
                    loading="lazy"
                    className="h-full w-full object-cover transition-[transform,filter] duration-700 group-hover:scale-105 group-hover:brightness-110"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center">
                    <Music2 className="h-10 w-10 text-zinc-700" />
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-6">
                  <div>
                    <p className="font-display text-2xl font-black tracking-tight">{a.artist_name}</p>
                    <p className="mt-1 text-[11px] uppercase tracking-[0.2em] text-zinc-400">
                      {a.track_count} morceau{a.track_count > 1 ? "x" : ""}
                    </p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-amber-500 transition-transform group-hover:translate-x-1" />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      )}
    </main>
  );
};

export default Artistes;
